import React, { useContext } from 'react';
import strings from 'common/strings';
import { formatMileage } from 'utils/formatter';
import TitleTooltip from 'components/shared/TitleTooltip';
import { ContentCopy } from 'components/shared/icons';
import { copyToClipboard } from 'utils/vehicle';
import { testIds, generateTestId } from 'common/testIds';
import VehicleCardSkeleton from './VehicleCardSkeleton';
import { VehicleCardContext } from '../VehicleCard';

interface WrapConditionalContainerProps {
  children: React.ReactNode;
  optionalClass: string;
  keyProperty: boolean;
}

var WrapConditionalContainer = ({
  children,
  optionalClass,
  keyProperty,
}: WrapConditionalContainerProps) => {
  if (keyProperty) {
    return <>{children}</>;
  }

  return <div className={optionalClass}>{children}</div>;
};

const VehicleDetailsTable = () => {
  const { vehicle, isShowroomMode, inExtensionCardMode, position } =
    useContext(VehicleCardContext);
  const disposition =
    vehicle?.vehicleCard?.disposition?.toLowerCase() ?? strings.EMPTY_VALUE;
  const color =
    vehicle?.vehicleCard?.exteriorColor?.toLowerCase() ?? strings.EMPTY_VALUE;
  const driveTrain =
    vehicle?.vehicleCard?.driveTrain?.toLowerCase() ?? strings.EMPTY_VALUE;

  return (
    <div
      className={`VehicleCard-details-table ${
        isShowroomMode ? 'details-table-showroom' : ''
      } `}
    >
      <div className="VehicleCard-detail-item">
        <div className="VehicleCard-label">
          <TitleTooltip label={strings.VIN} title={strings.VIN_TOOLTIP} />
        </div>
        <VehicleCardSkeleton renderSkeleton={!vehicle}>
          <div
            data-vas-testing={generateTestId(testIds.INVENTORY_SRP_CARD_VIN, {
              position: position.toString(),
            })}
            className={`VehicleCard-value VehicleCard-value-long ${
              !inExtensionCardMode && 'clickable'
            }`}
          >
            {vehicle?.vehicleCard.vin ? (
              <div
                onClick={() => copyToClipboard(vehicle.vehicleCard.vin)}
                role="none"
              >
                <span className="copy-content">{vehicle.vehicleCard.vin}</span>
                {!inExtensionCardMode && (
                  <ContentCopy className="VehicleCard-value-link" />
                )}
              </div>
            ) : (
              strings.EMPTY_VALUE
            )}
          </div>
        </VehicleCardSkeleton>
      </div>
      {!inExtensionCardMode && (
        <WrapConditionalContainer
          optionalClass="d-flex"
          keyProperty={isShowroomMode}
        >
          <WrapConditionalContainer
            optionalClass="col-mi-dlr"
            keyProperty={isShowroomMode}
          >
            <div className="VehicleCard-detail-item">
              <div className="VehicleCard-label">
                <TitleTooltip label={strings.STK} title={strings.STK_TOOLTIP} />
              </div>
              <VehicleCardSkeleton renderSkeleton={!vehicle}>
                <div
                  data-vas-testing={generateTestId(
                    testIds.INVENTORY_SRP_CARD_STOCK_NUMBER,
                    {
                      position: position.toString(),
                    }
                  )}
                  className="VehicleCard-value VehicleCard-value-long"
                >
                  <TitleTooltip
                    label={vehicle?.vehicleCard.stockNumber!}
                    title={vehicle?.vehicleCard.stockNumber!}
                  />
                </div>
              </VehicleCardSkeleton>
            </div>
            <div className="VehicleCard-detail-item">
              <div className="VehicleCard-label">
                <TitleTooltip
                  label={strings.MIL_SRP_CARD}
                  title={strings.MIL_TOOLTIP}
                />
              </div>
              <VehicleCardSkeleton renderSkeleton={!vehicle}>
                <div className="VehicleCard-value">
                  {formatMileage(
                    vehicle?.vehicleCard.mileage,
                    vehicle?.vehicleCard.odometerUnit,
                    false
                  )}
                </div>
              </VehicleCardSkeleton>
            </div>
            <div className="VehicleCard-detail-item">
              <div className="VehicleCard-label">
                <TitleTooltip label={strings.DLR} title={strings.DLR_TOOLTIP} />
              </div>
              <VehicleCardSkeleton renderSkeleton={!vehicle}>
                <div className="VehicleCard-value">
                  {vehicle?.vehicleCard.location.name}
                </div>
              </VehicleCardSkeleton>
            </div>
          </WrapConditionalContainer>
          <WrapConditionalContainer
            optionalClass="col-mi-dlr"
            keyProperty={isShowroomMode}
          >
            <div className="VehicleCard-detail-item">
              <div className="VehicleCard-label">
                <TitleTooltip label={strings.EXT} title={strings.EXT_TOOLTIP} />
              </div>
              <VehicleCardSkeleton renderSkeleton={!vehicle}>
                <div className="VehicleCard-value">{color}</div>
              </VehicleCardSkeleton>
            </div>
            {disposition && (
              <div className="VehicleCard-detail-item">
                <div className="VehicleCard-label">
                  <TitleTooltip
                    label={strings.DIS}
                    title={strings.DIS_TOOLTIP}
                  />
                </div>
                <VehicleCardSkeleton renderSkeleton={!vehicle}>
                  <div className="VehicleCard-value">{disposition}</div>
                </VehicleCardSkeleton>
              </div>
            )}
            {driveTrain && (
              <div className="VehicleCard-detail-item">
                <div className="VehicleCard-label">
                  <TitleTooltip
                    label={strings.DRIVETRAIN_LABEL}
                    title={strings.DRIVETRAIN_TOOLTIP}
                  />
                </div>
                <VehicleCardSkeleton renderSkeleton={!vehicle}>
                  <div className="VehicleCard-value">{driveTrain}</div>
                </VehicleCardSkeleton>
              </div>
            )}
          </WrapConditionalContainer>
        </WrapConditionalContainer>
      )}
    </div>
  );
};

export default VehicleDetailsTable;
